import { useEffect, useState } from 'react'
import styles from './Boot.module.css'
import { useT } from '../../../lib/i18n'

/**
 * POWER THE DESK (brief §6.1/§6.2).
 *
 * The desk coming alive: the strip dark, then the carrier, then the protocol
 * clock resolving to real numbers. Diegetic — it is the equipment warming, never
 * a loading screen, and it never says a word about the game.
 *
 * NEVER FORCE A WATCH. Any interaction ends it at once (§6.1), and it ends
 * itself on a short timer regardless. Both paths call `onDone` exactly once.
 *
 * TWO SHAPES, ONE COMPONENT (§6.2):
 *
 *   arranged — the pack's first contact is already agreed (Silfurberg). The boot
 *              resolves into the protocol clock and the desk waits WITH you.
 *   blind    — `openingWaitsForPlayer`: the scan opening. Nothing comes until the
 *              player transmits, so the boot resolves into stated silence rather
 *              than a countdown. The stillness law: silence is established before
 *              the first signal, and the surface's job is to make the limit
 *              legible so the silence can mean something.
 *
 * ⚠ THE SELECTOR IS NOT ON THE WIRE. `OpeningWaitsForPlayer` is a real pack field
 * (PackDefinition) but `/api/packs` does not project it and `/state` does not
 * carry it, so the client cannot currently tell which shape a pack wants. Both
 * variants are built and driven by this prop; what is missing is one field on the
 * pack card, which is server work and out of this mission's scope. Until it
 * lands, acceptance check 7 ("switching packs switches choreography with no code
 * change") is met by the COMPONENT and not by the system — flagged, not faked:
 * inferring the shape from, say, an empty backlog would be the client making a
 * semantic decision, and it would be wrong on every resumed blind session.
 */
export default function Boot({
  shape,
  onDone,
}: {
  shape: 'arranged' | 'blind'
  onDone: () => void
}) {
  const t = useT()
  // Three beats: dark → carrier → resolved. Held in one counter so the sequence
  // is readable as a sequence rather than as three racing timers.
  const [beat, setBeat] = useState(0)

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setBeat(1), 500),
      window.setTimeout(() => setBeat(2), 1150),
      window.setTimeout(onDone, 2100),
    ]
    return () => timers.forEach(window.clearTimeout)
  }, [onDone])

  return (
    <div
      className={styles.boot}
      onClick={onDone}
      onKeyDown={onDone}
      role="button"
      tabIndex={0}
      aria-label={t('desk.boot.aria')}
    >
      <div className={styles.panel}>
        <ol className={styles.lines}>
          <li className={beat >= 0 ? styles.on : styles.off}>{t('desk.boot.power')}</li>
          <li className={beat >= 1 ? styles.on : styles.off}>
            {t('desk.boot.carrier')}
            {beat >= 1 && <span className={styles.value}>{t('desk.boot.up')}</span>}
          </li>
          <li className={beat >= 2 ? styles.on : styles.off}>
            {t('desk.boot.link')}
            {beat >= 2 && (
              <span className={styles.value}>
                {shape === 'arranged' ? t('desk.boot.skedStanding') : t('desk.boot.noTraffic')}
              </span>
            )}
          </li>
        </ol>
      </div>
    </div>
  )
}
